// Learn article JSON-LD, shared by the prerender build (through the SSR
// bundle's re-export) and RouteMetadata so the two never drift. Built from the
// catalog and author facts only, so it is shell-safe. No FAQPage: the related
// questions are visible HTML, not a rich-result claim.
import { SITE_ORIGIN } from '../../lib/analytics/publicRoutes';

import { LEARN_AUTHORS, type LearnAuthor } from './authors';
import { LEARN_CATALOG, learnHeadline, type LearnCatalogEntry } from './catalog';
import { LEARN_INDEX_PATH, isLearnPublished, learnArticlePath, learnSlugFromPath } from './publication';

export const LEARN_SCHEMA_ID = 'lw-learn-schema';

export function buildLearnArticleGraph(input: { entry: LearnCatalogEntry; author: LearnAuthor }): object {
  const { entry, author } = input;
  const pageUrl = `${SITE_ORIGIN}${learnArticlePath(entry.slug)}`;
  const articleId = `${pageUrl}#article`;
  const webPageId = `${pageUrl}#webpage`;
  const breadcrumbId = `${pageUrl}#breadcrumb`;
  const personId = `${SITE_ORIGIN}${author.profilePath}#person`;
  const organizationId = `${SITE_ORIGIN}/#organization`;
  const headline = learnHeadline(entry);

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        '@id': articleId,
        headline,
        description: entry.description,
        url: pageUrl,
        mainEntityOfPage: { '@id': webPageId },
        datePublished: entry.publishedOn,
        dateModified: entry.updatedOn,
        inLanguage: 'en-US',
        author: { '@id': personId },
        publisher: { '@id': organizationId }
      },
      {
        '@type': 'WebPage',
        '@id': webPageId,
        url: pageUrl,
        name: entry.title,
        description: entry.description,
        inLanguage: 'en-US',
        mainEntity: { '@id': articleId },
        breadcrumb: { '@id': breadcrumbId },
        publisher: { '@id': organizationId }
      },
      {
        '@type': 'Person',
        '@id': personId,
        name: author.name,
        jobTitle: author.title,
        url: `${SITE_ORIGIN}${author.profilePath}`,
        worksFor: { '@id': organizationId },
        identifier: { '@type': 'PropertyValue', propertyID: 'NMLS', value: author.nmls }
      },
      {
        '@type': 'BreadcrumbList',
        '@id': breadcrumbId,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_ORIGIN}/` },
          { '@type': 'ListItem', position: 2, name: 'Learn', item: `${SITE_ORIGIN}${LEARN_INDEX_PATH}` },
          { '@type': 'ListItem', position: 3, name: headline, item: pageUrl }
        ]
      },
      {
        '@type': 'Organization',
        '@id': organizationId,
        name: 'Lendwise Mortgage',
        legalName: 'Lendwise Mortgage Inc.',
        url: `${SITE_ORIGIN}/`,
        logo: `${SITE_ORIGIN}/assets/lendwise-logo-green-bg.jpg`,
        identifier: { '@type': 'PropertyValue', propertyID: 'NMLS', value: '2702455' }
      }
    ]
  };
}

/**
 * Null off the Learn paths, for the index, for unknown slugs, and for a held
 * article in every environment: a held path renders the 404 page, and
 * emitting an Article for it would describe a document that is not there.
 */
export function learnStructuredDataGraph(pathname: string): object | null {
  const slug = learnSlugFromPath(pathname);
  if (!slug || !isLearnPublished(slug)) return null;
  const entry = LEARN_CATALOG.find(candidate => candidate.slug === slug);
  if (!entry?.publishedOn || !entry.updatedOn) return null;
  return buildLearnArticleGraph({ entry, author: LEARN_AUTHORS['anthony-amini'] });
}
